import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  HiOutlineViewGrid,
  HiOutlineViewList,
  HiPlus,
  HiOutlineEmojiSad,
  HiOutlineFilter,
  HiCloudUpload,
  HiOutlineArrowLeft,
  HiOutlineArrowRight,
  HiOutlineChevronRight,
  HiX,
  HiOutlineDotsVertical,
  HiOutlineShare,
  HiDownload,
  HiOutlineCheckCircle,
  HiOutlineTrash,
  HiOutlineChevronUp,
  HiOutlineChevronDown,
  HiCheckCircle,
  HiExclamationCircle,
  HiClock
} from "react-icons/hi";
import { motion, AnimatePresence } from "framer-motion";
import FileCard from "../Components/FileCard";
import FolderCard from "../Components/FolderCard";
import CreateFolderModal from "../Components/CreateFolderModal";
import ViewFileModal from "../Components/ViewFileModal";
import RenameModal from "../Components/RenameModal";
import ShareModal from "../Components/ShareModal";
import DownloadConfirmModal from "../Components/DownloadConfirmModal";
import DeleteModal from "../Components/DeleteModal";
import {
  getFoldersAPI,
  getFolderContentAPI,
  uploadFileAPI,
  renameFileAPI,
  renameFolderAPI,
  grantAccessAPI,
  deleteFolderAPI,
  deleteFileAPI,
  requestDownloadAPI,
  getDownloadUrl
} from "../services/driveService";
import { toast } from "react-toastify";
import useAuth from "../useAuth";
import { useNavigate } from "react-router-dom";
import { useActivity } from "../ActivityContext";

export default function Homepage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [viewSize, setViewSize] = useState(() => {
    const saved = localStorage.getItem("drive_viewSize");
    return (saved === "big" ? "small" : saved) || "small";
  });
  const [activeCategory, setActiveCategory] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewingFile, setViewingFile] = useState(null);
  const [renamingItem, setRenamingItem] = useState(null);
  const [sharingItem, setSharingItem] = useState(null);
  const [downloadItem, setDownloadItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("drive_sortBy") || "name");
  const [sortOrder, setSortOrder] = useState(() => localStorage.getItem("drive_sortOrder") || "asc");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef(null);
  const { user } = useAuth();
  const { refreshActivityReport } = useActivity();
  const navigate = useNavigate();

  // Navigation State
  const [currentFolderId, setCurrentFolderId] = useState(() => {
    try {
      const saved = localStorage.getItem("drive_currentFolderId");
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  });

  const [breadcrumbs, setBreadcrumbs] = useState(() => {
    try {
      const saved = localStorage.getItem("drive_breadcrumbs");
      return saved ? JSON.parse(saved) : [{ id: null, name: "Home" }];
    } catch (e) {
      return [{ id: null, name: "Home" }];
    }
  });

  useEffect(() => {
    localStorage.setItem("drive_currentFolderId", JSON.stringify(currentFolderId));
  }, [currentFolderId]);

  useEffect(() => {
    localStorage.setItem("drive_viewSize", viewSize);
  }, [viewSize]);

  useEffect(() => {
    localStorage.setItem("drive_breadcrumbs", JSON.stringify(breadcrumbs));
  }, [breadcrumbs]);

  useEffect(() => {
    localStorage.setItem("drive_sortOrder", sortOrder);
  }, [sortOrder]);

  useEffect(() => {
    localStorage.setItem("drive_sortBy", sortBy);
  }, [sortBy]);

  useEffect(() => {
    // Initialize history state on load if empty, so we have a base to 'go back' to
    if (!window.history.state || !window.history.state.breadcrumbs) {
      window.history.replaceState({ breadcrumbs, folderId: currentFolderId }, "");
    }

    const handlePopState = (event) => {
      if (event.state && event.state.breadcrumbs) {
        setCurrentFolderId(event.state.folderId);
        setBreadcrumbs(event.state.breadcrumbs);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Real Data State
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeUploads, setActiveUploads] = useState([]); // [{ id, name, progress, status, size }]
  const [isUploadPanelMinimized, setIsUploadPanelMinimized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const uploadAbortControllerRef = useRef(null);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 B";
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const totalSelectedSize = useMemo(() => {
    let total = 0;
    selectedItems.forEach(key => {
      const [type, id] = key.split(':');
      if (type === 'folder') {
        const folder = folders.find(f => String(f.id) === String(id));
        if (folder) total += (folder.size || 0);
      } else {
        const file = files.find(f => String(f.id) === String(id));
        if (file) total += (file.size || 0);
      }
    });
    return total;
  }, [selectedItems, folders, files]);

  const fetchData = async () => {
    setLoadingData(true);
    try {
      if (currentFolderId === null) {
        console.log("Fetching folders with sort:", sortBy, sortOrder);
        const data = await getFoldersAPI(sortBy, sortOrder);
        setFolders(data?.folders || []);
        setFiles(data?.files || []);
      } else {
        console.log("Fetching folder content with sort:", sortBy, sortOrder);
        const data = await getFolderContentAPI(currentFolderId, sortBy, sortOrder);
        setFolders(data?.folders || []);
        setFiles(data?.files || []);
      }
    } catch (error) {
      toast.error("Failed to load content");
      console.error(error);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchData();
    const handleRefresh = () => fetchData();
    const handleResetHome = () => {
      const newBreadcrumbs = [{ id: null, name: "Home" }];
      window.history.pushState({ breadcrumbs: newBreadcrumbs, folderId: null }, "");
      setCurrentFolderId(null);
      setBreadcrumbs(newBreadcrumbs);
    };

    window.addEventListener("folderCreated", handleRefresh);
    window.addEventListener("resetToHomeFolders", handleResetHome);

    return () => {
      window.removeEventListener("folderCreated", handleRefresh);
      window.removeEventListener("resetToHomeFolders", handleResetHome);
    };
  }, [currentFolderId, sortBy, sortOrder]);

  const handleFolderClick = (folder) => {
    const newBreadcrumbs = [...breadcrumbs, { id: folder.id, name: folder.name }];
    window.history.pushState({ breadcrumbs: newBreadcrumbs, folderId: folder.id }, "");
    setCurrentFolderId(folder.id);
    setBreadcrumbs(newBreadcrumbs);
  };

  const handleBreadcrumbClick = (id, index) => {
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
    window.history.pushState({ breadcrumbs: newBreadcrumbs, folderId: id }, "");
    setCurrentFolderId(id);
    setBreadcrumbs(newBreadcrumbs);
  };

  const handleBack = () => {
    if (breadcrumbs.length > 1) {
      handleBreadcrumbClick(breadcrumbs[breadcrumbs.length - 2].id, breadcrumbs.length - 2);
    }
  };
  const handleForward = () => window.history.forward();

  const handleCancelUpload = () => {
    activeUploads.forEach(u => {
      if (u.status === 'uploading' || u.status === 'pending') {
        u.abortController.abort();
      }
    });
    setUploading(false);
    toast.info("All uploads cancelled");
  };

  const handleCancelIndividualUpload = (id) => {
    const upload = activeUploads.find(u => u.id === id);
    if (upload && (upload.status === 'uploading' || upload.status === 'pending')) {
      upload.abortController.abort();
      setActiveUploads(prev => prev.map(u =>
        u.id === id ? { ...u, status: 'cancelled' } : u
      ));
      toast.info(`Upload of ${upload.name} cancelled`);
    }
  };

  const handleToggleSelect = (item, type) => {
    const itemKey = `${type}:${item.id}`;
    setSelectedItems(prev =>
      prev.includes(itemKey)
        ? prev.filter(k => k !== itemKey)
        : [...prev, itemKey]
    );
  };

  const handleSelectAll = () => {
    const allKeys = [
      ...filteredFolders.map(f => `folder:${f.id}`),
      ...filteredFiles.map(f => `file:${f.id}`)
    ];
    if (selectedItems.length === allKeys.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(allKeys);
    }
  };

  const handleBatchDownload = async () => {
    const selectedFiles = files.filter(f => selectedItems.includes(`file:${f.id}`));
    const selectedFoldersCount = selectedItems.filter(k => k.startsWith('folder:')).length;

    if (selectedFiles.length === 0) {
      if (selectedFoldersCount > 0) {
        toast.info("Only files can be downloaded directly. Folders cannot be downloaded.");
      }
      return;
    }

    if (selectedFoldersCount > 0) {
      toast.warning(`${selectedFoldersCount} folders were skipped. Only files can be downloaded directly.`);
    }

    toast.info(`Preparing ${selectedFiles.length} files for download...`);
    const token = localStorage.getItem("dbToken");

    for (const file of selectedFiles) {
      try {
        const requestData = await requestDownloadAPI(file.id);
        const tempGuid = requestData?.tempFileGuid || requestData?.data?.tempFileGuid;
        if (tempGuid) {
          const finalUrl = `${getDownloadUrl(tempGuid)}?token=${token}`;
          const link = document.createElement("a");
          link.href = finalUrl;
          link.download = file.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          refreshActivityReport();
        }
      } catch (err) {
        console.error("Batch download failed for", file.name, err);
      }
      // Small delay to prevent browser download protection from blocking multiple popups
      await new Promise(r => setTimeout(r, 1000));
    }
    setSelectedItems([]);
  };

  const handleBatchDelete = () => {
    const count = selectedItems.length;
    if (count === 0) return;

    // For simplicity, we'll use the existing delete modal logic but adapted
    // or just show a custom toast/confirmation.
    // Here we'll just set the first item to delete and handle it as a batch in confirm
    setItemToDelete({ id: "batch", name: `${count} selected items`, type: "batch" });
    setIsDeleteModalOpen(true);
  };

  const handleBatchShare = () => {
    const itemsToShare = selectedItems.map(key => {
      const [type, id] = key.split(':');
      const item = type === 'folder'
        ? folders.find(f => String(f.id) === String(id))
        : files.find(f => String(f.id) === String(id));
      return { ...item, type };
    }).filter(item => item.id); // ensure we have valid items

    if (itemsToShare.length > 0) {
      setSharingItem(itemsToShare);
    }
  };

  const handleFileUpload = async (e) => {
    const selectedFiles = (e.target && e.target.files) ? Array.from(e.target.files) : Array.from(e);
    if (selectedFiles.length === 0) return;

    // Initialize uploads in the panel
    const newUploads = selectedFiles.map((file, index) => ({
      id: Date.now() + index,
      name: file.name,
      progress: 0,
      status: 'pending',
      size: file.size,
      abortController: new AbortController()
    }));

    setActiveUploads(prev => [...newUploads, ...prev]);
    setUploading(true);

    for (const uploadItem of newUploads) {
      const file = selectedFiles[newUploads.indexOf(uploadItem)];

      setActiveUploads(prev => prev.map(u =>
        u.id === uploadItem.id ? { ...u, status: 'uploading' } : u
      ));

      try {
        await uploadFileAPI(file, currentFolderId, (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setActiveUploads(prev => prev.map(u =>
            u.id === uploadItem.id ? { ...u, progress: percentCompleted } : u
          ));
        }, uploadItem.abortController.signal, false);

        setActiveUploads(prev => prev.map(u =>
          u.id === uploadItem.id ? { ...u, status: 'success', progress: 100 } : u
        ));
        refreshActivityReport();
      } catch (error) {
        if (error.name === 'CanceledError') {
          setActiveUploads(prev => prev.map(u =>
            u.id === uploadItem.id ? { ...u, status: 'cancelled' } : u
          ));
        } else {
          setActiveUploads(prev => prev.map(u =>
            u.id === uploadItem.id ? { ...u, status: 'error' } : u
          ));
          toast.error(`Failed to upload ${file.name}`);
        }
      }
    }

    // toast.success(`Upload process completed!`);
    fetchData();
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      handleFileUpload(droppedFiles);
    }
  };

  const handleRename = async (newName) => {
    if (!renamingItem) return;
    try {
      // Use explicit type set in onRename handler
      if (renamingItem.type === "file") {
        await renameFileAPI(renamingItem.id, newName);
      } else {
        await renameFolderAPI(renamingItem.id, newName);
      }
      toast.success("Renamed successfully!");
      refreshActivityReport();
      fetchData();
    } catch (error) {
      toast.error(error?.message || "Rename failed");
    }
  };

  const handleShareSubmit = async (userIds, resources, permission) => {
    try {
      await grantAccessAPI(userIds, resources, permission);
      toast.success(`Access granted successfully`);
      refreshActivityReport();
      setSharingItem(null);
      setSelectedItems([]);
    } catch (error) {
      toast.error(error?.message || "Failed to grant access");
    }
  };




  const handleDeleteFolder = (folder) => {
    setItemToDelete({ ...folder, type: "folder" });
    setIsDeleteModalOpen(true);
  };

  const handleDeleteFile = (file) => {
    setItemToDelete({ ...file, type: "file" });
    setIsDeleteModalOpen(true);
  };

  const confirmItemDeletion = async (item, type) => {
    setIsDeleting(true);
    try {
      if (type === "batch") {
        let successCount = 0;
        for (const itemKey of selectedItems) {
          try {
            const [itemType, itemId] = itemKey.split(":");
            if (itemType === "folder") {
              await deleteFolderAPI(itemId);
            } else {
              await deleteFileAPI(itemId);
            }
            successCount++;
          } catch (err) {
            console.error("Batch delete failed for", itemKey, err);
          }
        }
        toast.success(`${successCount} items moved to trash`);
        setSelectedItems([]);
      } else if (type === "folder") {
        await deleteFolderAPI(item.id);
        toast.success(`Folder moved to trash`);
      } else {
        await deleteFileAPI(item.id);
        toast.success(`File moved to trash`);
      }
      refreshActivityReport();
      fetchData();
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
    } catch (error) {
      toast.error(error?.message || `Failed to delete items`);
    } finally {
      setIsDeleting(false);
    }
  };

  const categories = [
    { id: "all", label: "All Files" },
    { id: "document", label: "Documents" },
    { id: "image", label: "Images" },
  ];

  const isSearching = searchQuery.length > 0 || activeCategory !== "all";

  const filteredFolders = useMemo(() => {
    // When searching or viewing categories, show items regardless of folder depth
    if (isSearching) return folders;

    return folders.filter(f => {
      // Direct comparison handling both null and undefined for root level
      const folderParentId = f.parentId || null;
      return folderParentId === currentFolderId;
    });
  }, [folders, currentFolderId, isSearching]);

  const filteredFiles = useMemo(() => {
    return files.filter(file => {
      // 1. Folder Context Filter (If NOT searching, only show files in current folder)
      if (!isSearching) {
        const fileFolderId = file.folderId || null;
        if (fileFolderId !== currentFolderId) return false;
      }

      // 2. Metadata & Text Filters
      const matchesSearch = file.name.toLowerCase().includes(searchQuery.toLowerCase());
      const contentType = file.contentType || "";
      const extension = (file.extension || file.name.split('.').pop()).toLowerCase();

      const isDoc = contentType.includes("pdf") || contentType.includes("word") || ["pdf", "doc", "docx", "txt"].includes(extension);
      const isImg = contentType.includes("image") || ["jpg", "jpeg", "png", "gif", "svg"].includes(extension);

      if (activeCategory === "document") return isDoc && matchesSearch;
      if (activeCategory === "image") return isImg && matchesSearch;

      return matchesSearch;
    });
  }, [searchQuery, activeCategory, files, currentFolderId, isSearching]);

  return (
    <div
      className="relative min-h-screen"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Premium Full-Page Loader */}
      <AnimatePresence>
        {loadingData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2000] bg-white/60 backdrop-blur-md flex flex-col items-center justify-center gap-6"
          >
            <div className="relative">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                className="w-20 h-20 border-4 border-slate-100 border-t-indigo-600 rounded-full"
              />
              <motion.div
                animate={{ scale: [0.8, 1.1, 0.8], opacity: [0.5, 1, 0.5] }}
                transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                className="absolute inset-0 m-auto w-10 h-10 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-200"
              >
                <HiCloudUpload className="text-white text-xl" />
              </motion.div>
            </div>
            <div className="text-center">
              <h2 className="text-lg font-black text-slate-900 uppercase tracking-tighter">Syncing Content</h2>
              <div className="flex items-center gap-2 justify-center mt-1">
                <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-bounce"></span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Drag & Drop Overlay */}
      <AnimatePresence>
        {isDragging && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1000] bg-indigo-600/10 backdrop-blur-md flex items-center justify-center p-8 pointer-events-none"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white border-4 border-dashed border-indigo-600/30 rounded-[48px] p-12 flex flex-col items-center gap-6 shadow-2xl"
            >
              <div className="w-24 h-24 rounded-3xl bg-indigo-600 text-white flex items-center justify-center shadow-indigo-200 shadow-2xl">
                <HiCloudUpload size={48} className="animate-bounce" />
              </div>
              <div className="text-center space-y-2">
                <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">Release to Upload</h2>
                <p className="text-slate-500 font-bold uppercase tracking-widest text-sm">Drop your files anywhere to start the sync</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        className="hidden"
        multiple
      />

      {/* Greeting Section - Clean & Focused */}
      <section className="px-2 pt-2">
        <h1 className="text-2xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-2">
          {user?.fullName?.split(' ')[0] || "---"}'s <span className="text-indigo-600/70 font-medium whitespace-nowrap">Drive</span>
        </h1>
        <p className="text-slate-500 text-[10px] md:text-sm font-bold uppercase tracking-[0.2em]">Manage your cloud assets with precision</p>
      </section>

      {/* Top Navigation & Breadcrumbs Bar */}
      <section className="py-2">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between bg-white border border-slate-200 rounded-2xl md:rounded-[24px] p-2 gap-3 lg:gap-0 lg:pr-6 shadow-xl overflow-hidden">
          <div className="flex items-center gap-2 overflow-hidden">
            <div className="flex items-center bg-slate-100 rounded-xl p-1 border border-slate-200 shrink-0">
              <button onClick={handleBack} className="p-2 text-slate-500 hover:text-slate-900 transition-all"><HiOutlineArrowLeft size={18} /></button>
              <button onClick={handleForward} className="p-2 text-slate-500 hover:text-slate-900 transition-all border-l border-slate-200"><HiOutlineArrowRight size={18} /></button>
            </div>

            <div className="flex items-center gap-1 overflow-x-auto no-scrollbar mask-fade-right">
              {breadcrumbs.map((b, i) => (
                <React.Fragment key={b.id || i}>
                  <button
                    onClick={() => handleBreadcrumbClick(b.id, i)}
                    className={`text-[9px] md:text-[10px] font-bold uppercase tracking-widest px-2.5 md:px-3 py-2 rounded-lg transition-all whitespace-nowrap flex items-center gap-2 ${i === breadcrumbs.length - 1 ? "text-indigo-600 bg-indigo-50" : "text-slate-500 hover:text-slate-900"
                      }`}
                  >
                    {b.name}
                    {i === breadcrumbs.length - 1 && b.id !== null && (
                      <div className="flex items-center gap-1 ml-2 pl-2 border-l border-white/10">
                        <button onClick={(e) => { e.stopPropagation(); setRenamingItem({ id: b.id, name: b.name }); }} title="Rename Folder" className="hover:text-blue-400 transition-colors"><HiOutlineDotsVertical size={12} /></button>
                        <button onClick={(e) => { e.stopPropagation(); setSharingItem({ id: b.id, name: b.name, type: "folder" }); }} title="Share Folder" className="hover:text-[#F43F5E] transition-colors"><HiOutlineShare size={12} /></button>
                      </div>
                    )}
                  </button>
                  {i < breadcrumbs.length - 1 && <HiOutlineChevronRight className="text-slate-300" size={10} />}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between lg:justify-end gap-2 px-1 lg:px-0">
            {/* Multi-Selection Actions Bar */}
            <AnimatePresence>
              {selectedItems.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="flex items-center gap-1 bg-indigo-600 p-1 rounded-xl shadow-lg shadow-indigo-100"
                >
                  <div className="px-3 py-1 flex items-center gap-2 border-r border-white/20">
                    <HiOutlineCheckCircle className="text-white" size={16} />
                    <div className="flex flex-col leading-tight">
                      <span className="text-[10px] font-black text-white uppercase tracking-widest">{selectedItems.length} SELECTED</span>
                      <span className="text-[8px] font-bold text-indigo-200 uppercase tracking-tighter">{formatFileSize(totalSelectedSize)}</span>
                    </div>
                  </div>
                  <button
                    onClick={handleBatchDownload}
                    className="p-2 text-white hover:bg-white/10 rounded-lg transition-all"
                    title="Download Selected"
                  >
                    <HiDownload size={18} />
                  </button>
                  <button
                    onClick={handleBatchShare}
                    className="p-2 text-white hover:bg-white/10 rounded-lg transition-all"
                    title="Share Selected"
                  >
                    <HiOutlineShare size={18} />
                  </button>
                  <button
                    onClick={handleBatchDelete}
                    className="p-2 text-white hover:bg-white/10 rounded-lg transition-all"
                    title="Delete Selected"
                  >
                    <HiOutlineTrash size={18} />
                  </button>
                  <button
                    onClick={() => setSelectedItems([])}
                    className="p-2 text-white hover:bg-white/10 rounded-lg transition-all border-l border-white/20"
                    title="Clear Selection"
                  >
                    <HiX size={16} />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex items-center gap-1 bg-slate-100/80 p-1 rounded-xl border border-slate-200/60 shadow-sm backdrop-blur-sm">
              <div className="flex items-center pl-2.5 pr-1 border-r border-slate-200/80 gap-2">
                {loadingData ? (
                  <div className="w-3.5 h-3.5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <HiOutlineFilter size={13} className="text-slate-400" />
                )}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-transparent text-[10px] font-black uppercase tracking-wider focus:outline-none cursor-pointer text-slate-700 hover:text-indigo-600 transition-colors appearance-none pr-4 relative"
                  style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%2394a3b8\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right center', backgroundSize: '10px' }}
                >
                  <option value="name">Name</option>
                  <option value="date">Date</option>
                  <option value="size">Size</option>
                  <option value="type">Type</option>
                </select>
              </div>
              <div className="px-2">
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="bg-transparent text-[10px] font-black uppercase tracking-wider focus:outline-none cursor-pointer text-slate-700 hover:text-indigo-600 transition-colors appearance-none pr-4"
                  style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%2394a3b8\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right center', backgroundSize: '10px' }}
                >
                  <option value="asc">ASC</option>
                  <option value="desc">DESC</option>
                </select>
              </div>
            </div>

            <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200 order-2 lg:order-1">
              <button onClick={() => setViewSize("small")} className={`p-1.5 md:p-2 rounded-lg transition-all ${viewSize === "small" ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500"}`} title="Small Grid"><HiOutlineViewGrid size={14} /></button>
              <button onClick={() => setViewSize("list")} className={`p-1.5 md:p-2 rounded-lg transition-all ${viewSize === "list" ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500"}`} title="List View"><HiOutlineViewList size={18} /></button>
            </div>

            <div className="flex items-center gap-2 order-1 lg:order-2 lg:ml-2">
              {[
                { label: "NEW FOLDER", icon: "📁", bg: "bg-blue-500/5", text: "text-blue-600", onClick: () => setIsModalOpen(true) },
              ].map((btn, i) => (
                <button
                  key={i}
                  onClick={btn.onClick}
                  className={`${btn.bg} ${btn.text} px-3 md:px-4 py-2 rounded-xl flex items-center gap-2 hover:bg-white hover:shadow-sm transition-all border border-transparent hover:border-slate-100`}
                >
                  <span className="text-base md:text-lg">{btn.icon}</span>
                  <span className="text-[9px] md:text-[10px] font-bold tracking-widest whitespace-nowrap">{btn.label}</span>
                </button>
              ))}

              <button
                onClick={() => fileInputRef.current?.click()}
                className="bg-resq-resqAccent hover:scale-105 active:scale-95 text-white text-[9px] md:text-[10px] uppercase tracking-widest px-4 md:px-5 py-2.5 rounded-xl transition-all shadow-lg flex items-center gap-2"
              >
                <HiCloudUpload size={16} className="shrink-0" />
                <span className="whitespace-nowrap">{uploading ? "Uploading..." : "Upload"}</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Grid */}
      <section className="flex flex-col">

        {/* Table Header for List View */}
        {/* {viewSize === "list" && (
          <div className="flex items-center px-6 py-2 border-b border-slate-200 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 sticky top-0 bg-[#F8FAFC]/80 backdrop-blur-md z-10">
            <div className="flex-1 flex items-center gap-3">
              <span className="w-8"></span>
              <span>Name</span>
            </div>
            <div className="w-40 hidden md:block">Modified</div>
            <div className="w-32 hidden lg:block">Type</div>
            <div className="w-24 text-right">Size</div>
            <div className="w-10"></div>
          </div>
        )} */}

        {/* Main Column: Folders & Files */}
        {/* Unified Folders & Files Container */}
        <div className={`${viewSize === "list" ? "pt-0" : "pt-2"}`}>
          <div
            key={`${sortOrder}-${sortBy}`}
            className={`grid ${viewSize === "list" ? "gap-0 grid-cols-1" : "gap-1.5 grid-cols-4 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12"}`}
          >
            <AnimatePresence>
              {/* Folders */}
              {filteredFolders.map((folder, idx) => (
                <FolderCard
                  key={`folder-${folder.id}`}
                  folder={folder}
                  index={idx}
                  view={viewSize === "list" ? "list" : "grid"}
                  isBig={viewSize === "big"}
                  onClick={() => handleFolderClick(folder)}
                  onRename={(item) => setRenamingItem({ ...item, type: "folder" })}
                  onShare={(item) => setSharingItem({ ...item, type: "folder" })}
                  onDelete={() => handleDeleteFolder(folder)}
                  isSelected={selectedItems.includes(`folder:${folder.id}`)}
                  onToggleSelect={() => handleToggleSelect(folder, "folder")}
                />
              ))}

              {/* Files - Flowing naturally after folders */}
              {filteredFiles.map((file, idx) => (
                <FileCard
                  key={`file-${file.id}`}
                  file={file}
                  index={idx + filteredFolders.length}
                  view={viewSize === "list" ? "list" : "grid"}
                  isBig={viewSize === "big"}
                  onView={() => setViewingFile(file)}
                  onRename={(item) => setRenamingItem({ ...item, type: "file" })}
                  onShare={(item) => setSharingItem({ ...item, type: "file" })}
                  onDelete={handleDeleteFile}
                  onDownloadClick={setDownloadItem}
                  isSelected={selectedItems.includes(`file:${file.id}`)}
                  onToggleSelect={() => handleToggleSelect(file, "file")}
                />
              ))}
            </AnimatePresence>
          </div>
        </div>
        {!loadingData && folders.length === 0 && filteredFiles.length === 0 && (
          <div className="py-20 flex flex-col items-center justify-center text-slate-600 gap-4 opacity-50">
            <HiOutlineEmojiSad size={48} />
            <p className="font-bold text-sm tracking-widest uppercase">No items found in this directory</p>
          </div>
        )}
      </section>

      {/* Multi-Upload Status Panel */}
      <AnimatePresence>
        {activeUploads.length > 0 && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            className={`fixed bottom-6 right-6 z-[200] bg-white border border-slate-200 shadow-2xl transition-all duration-500 overflow-hidden ${isUploadPanelMinimized ? "w-72 rounded-2xl" : "w-96 rounded-[28px]"
              }`}
          >
            {/* Header */}
            <div className={`flex items-center justify-between px-5 py-4 border-b border-slate-100 ${isUploadPanelMinimized ? "bg-slate-50/50" : "bg-white"}`}>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${uploading ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500"}`}>
                  <HiCloudUpload size={18} className={uploading ? "animate-bounce" : ""} />
                </div>
                <div>
                  <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">
                    {uploading ? "Uploading Assets" : "Uploads Complete"}
                  </h3>
                  {!isUploadPanelMinimized && (
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                      {activeUploads.filter(u => u.status === 'success').length} of {activeUploads.length} finished
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setIsUploadPanelMinimized(!isUploadPanelMinimized)}
                  className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 transition-colors"
                >
                  {isUploadPanelMinimized ? <HiOutlineChevronUp size={16} /> : <HiOutlineChevronDown size={16} />}
                </button>
                <button
                  onClick={() => setActiveUploads([])}
                  className="p-1.5 hover:bg-rose-50 rounded-lg text-slate-400 hover:text-rose-500 transition-colors"
                >
                  <HiX size={16} />
                </button>
              </div>
            </div>

            {/* List */}
            <motion.div
              initial={false}
              animate={{ height: isUploadPanelMinimized ? 0 : "auto", opacity: isUploadPanelMinimized ? 0 : 1 }}
              className="overflow-hidden bg-slate-50/30"
            >
              <div className="max-h-[360px] overflow-y-auto no-scrollbar p-3 space-y-2">
                {activeUploads.map((upload) => (
                  <div key={upload.id} className="bg-white border border-slate-100 p-3 rounded-2xl shadow-sm group hover:border-indigo-100 transition-colors">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${upload.status === 'success' ? 'bg-emerald-50 text-emerald-600' :
                        upload.status === 'error' ? 'bg-rose-50 text-rose-600' :
                          'bg-indigo-50 text-indigo-600'
                        }`}>
                        {upload.status === 'success' ? <HiCheckCircle size={18} /> :
                          upload.status === 'error' ? <HiExclamationCircle size={18} /> :
                            upload.status === 'pending' ? <HiClock size={18} /> :
                              <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center gap-2 mb-0.5">
                          <p className="text-[11px] font-bold text-slate-900 truncate uppercase tracking-tight">{upload.name}</p>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-black text-slate-400">{upload.progress}%</span>
                            {(upload.status === 'uploading' || upload.status === 'pending') && (
                              <button
                                onClick={() => handleCancelIndividualUpload(upload.id)}
                                className="p-1.5 hover:bg-rose-50 rounded-lg text-slate-400 hover:text-rose-500 transition-all border border-transparent hover:border-rose-100"
                                title="Cancel specific upload"
                              >
                                <HiX size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${upload.progress}%` }}
                            className={`h-full transition-colors duration-500 ${upload.status === 'success' ? 'bg-emerald-500' :
                              upload.status === 'error' ? 'bg-rose-500' :
                                'bg-indigo-600'
                              }`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modals */}
      <CreateFolderModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => fetchData()}
        parentId={currentFolderId}
      />

      <ViewFileModal
        file={viewingFile}
        isOpen={!!viewingFile}
        onClose={() => setViewingFile(null)}
      />

      <RenameModal
        isOpen={!!renamingItem}
        onClose={() => setRenamingItem(null)}
        currentName={renamingItem?.name}
        onRename={handleRename}
      />

      <DownloadConfirmModal
        isOpen={!!downloadItem}
        onClose={() => setDownloadItem(null)}
        file={downloadItem}
        onConfirm={() => {
          // We can't easily trigger handleDownload inside FileCard from here without refs.
          // The best way is to have the download logic in a central place or service.
          // I will implement a global download trigger or just let the FileCard handle its own download if we pass a 'trigger' prop.
          // Alternatively, I can just re-implement the download logic here since I have access to derviceService.
          window.dispatchEvent(new CustomEvent('triggerDownload', { detail: { fileId: downloadItem.id } }));
        }}
      />

      <ShareModal
        isOpen={!!sharingItem}
        onClose={() => setSharingItem(null)}
        resource={sharingItem}
        onShare={handleShareSubmit}
      />

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        item={itemToDelete}
        type={itemToDelete?.type}
        onConfirm={confirmItemDeletion}
        isProcessing={isDeleting}
      />
    </div>
  );
}

